<script setup lang="ts">
import { XMarkIcon, ShareIcon, PlusIcon } from '@heroicons/vue/24/outline';
import { usePwaInstall } from '@/composables/usePwaInstall';

const { showIosBanner, showAndroidInstall, dismiss, install } = usePwaInstall();

const handleInstall = async () => {
  await install();
};
</script>

<template>
  <!-- iOS Safari Install Banner -->
  <Transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
  >
    <div v-if="showIosBanner" class="fixed bottom-0 inset-x-0 z-50 p-4 pb-safe">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 mx-auto max-w-md">
        <!-- Close button -->
        <button
          type="button"
          class="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          @click="dismiss"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>

        <!-- Content -->
        <div class="flex items-start gap-4">
          <!-- App Icon -->
          <div class="flex-shrink-0">
            <img src="/icons/icon-72x72.png" alt="WingIt" class="w-14 h-14 rounded-xl shadow-md" />
          </div>

          <div class="flex-1 min-w-0">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Install WingIt</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Add to your home screen for the best experience</p>
          </div>
        </div>

        <!-- Instructions -->
        <div class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">To install this app:</p>
          <ol class="space-y-3">
            <li class="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span
                class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 flex items-center justify-center text-xs font-medium"
              >
                1
              </span>
              <span class="flex items-center gap-2">
                Tap the Share button
                <ShareIcon class="h-5 w-5 text-blue-500" />
              </span>
            </li>
            <li class="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span
                class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 flex items-center justify-center text-xs font-medium"
              >
                2
              </span>
              <span class="flex items-center gap-2">
                Scroll and tap "Add to Home Screen"
                <span class="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-200 dark:bg-gray-600">
                  <PlusIcon class="h-3 w-3 text-gray-600 dark:text-gray-300" />
                </span>
              </span>
            </li>
          </ol>
        </div>

        <!-- Dismiss link -->
        <div class="mt-4 text-center">
          <button type="button" class="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" @click="dismiss">Maybe later</button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Android/Chrome Install Banner -->
  <Transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
  >
    <div v-if="showAndroidInstall" class="fixed bottom-0 inset-x-0 z-50 p-4 pb-safe">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 mx-auto max-w-md">
        <!-- Close button -->
        <button
          type="button"
          class="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          @click="dismiss"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>

        <!-- Content -->
        <div class="flex items-center gap-4">
          <!-- App Icon -->
          <div class="flex-shrink-0">
            <img src="/icons/icon-72x72.png" alt="WingIt" class="w-14 h-14 rounded-xl shadow-md" />
          </div>

          <div class="flex-1 min-w-0">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Install WingIt</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get quick access from your home screen</p>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="mt-4 flex gap-3">
          <button
            type="button"
            class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            @click="dismiss"
          >
            Not now
          </button>
          <button
            type="button"
            class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
            @click="handleInstall"
          >
            Install
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.pb-safe {
  padding-bottom: max(1rem, env(safe-area-inset-bottom));
}
</style>
